import type {
	LiveSessionItem,
	LiveSessionResponse,
	SessionItem,
} from "@memorang/types/src/session/new-session";
import { mockedPraxisMixedItemDistribution } from "../praxis-mocked-mixeditemdist-response";
import module1 from "../proethica-live/module-1.json";
import module2 from "../proethica-live/module-2.json";
import module3 from "../proethica-live/module-3.json";
import module4 from "../proethica-live/module-4.json";
import module5 from "../proethica-live/module-5.json";
import module6 from "../proethica-live/module-6.json";
import module7 from "../proethica-live/module-7.json";
import module8 from "../proethica-live/module-8.json";
import {
	buildProethicaModule,
	buildProethicaTest,
} from "./build-proethica-module";
import {
	type ProethicaModuleSummary,
	type RawProethicaSessionItem,
	extractModuleSummary,
} from "./proethica";
import {
	type NewFormatModuleItem,
	findModuleOverview,
	isProethicaTestItem,
	normalizeModuleItems,
} from "./proethica-adapter";

const asEnvelope = (json: unknown) => json as unknown as LiveSessionResponse;

const moduleNumberFromLabel = (label?: string): number | undefined => {
	const match = label?.match(/Module\s+(\d+)/i);
	return match ? Number(match[1]) : undefined;
};

// Live create-session envelopes, indexed by the module number parsed from each
// envelope's examName ("Module 3: ...").
const envelopeByModuleNumber: Record<number, LiveSessionResponse> =
	Object.fromEntries(
		[module1, module2, module3, module4, module5, module6, module7, module8]
			.map(asEnvelope)
			.flatMap((env) => {
				const moduleNumber = moduleNumberFromLabel(env.examName);
				return moduleNumber ? [[moduleNumber, env] as const] : [];
			}),
	);

// Module 3 is the session shown when no specific module is selected.
export const proethicaDefaultEnvelope = asEnvelope(module3);

type ProethicaDisplayTag = {
	id: number;
	label?: string;
	title?: string;
	displayType?: string[];
};

const proethicaDisplayTags = (
	mockedPraxisMixedItemDistribution.data.tags as ProethicaDisplayTag[]
).filter((tag) => tag.displayType?.includes("Display"));

// The dashboard's module list is driven by the mixed-item-distribution mock,
// whose Display tags carry opaque ids (e.g. 37089) and a "Module N" label.
// Session selection stores that tag id as currentModuleId, so the live
// envelopes must be keyed by the SAME tag id, not the internal ProEthica
// moduleId. Bridge the two mock datasets on the module number both carry.
export const proethicaLiveEnvelopeByModule: Record<
	string,
	LiveSessionResponse
> = Object.fromEntries(
	proethicaDisplayTags.flatMap((tag) => {
		const moduleNumber = moduleNumberFromLabel(tag.label ?? tag.title);
		const env =
			moduleNumber != null ? envelopeByModuleNumber[moduleNumber] : undefined;
		return env ? [[String(tag.id), env] as const] : [];
	}),
);

// Live ProEthica module sessions are created SUMMATIVE/PRACTICE_TEST against the
// demo-content project (the recipe that captured the envelopes above).
export const proethicaLiveSessionConfig = {
	projectId: 26,
	organizationId: 24,
	sessionType: "SUMMATIVE",
	sessionSubType: "PRACTICE_TEST",
} as const;

export const proethicaCollectionIdByModule: Record<string, number> =
	Object.fromEntries(
		Object.entries(proethicaLiveEnvelopeByModule).flatMap(([tagId, env]) =>
			typeof env.collectionId === "number"
				? [[tagId, env.collectionId] as const]
				: [],
		),
	);

export const getProethicaLiveCreateParams = (moduleId?: string) => {
	const collectionId =
		(moduleId ? proethicaCollectionIdByModule[moduleId] : undefined) ??
		proethicaDefaultEnvelope.collectionId!;
	return { ...proethicaLiveSessionConfig, collectionId };
};

const asAdapterItems = (env: LiveSessionResponse) =>
	env.sessionItems as unknown as NewFormatModuleItem[];

const normalizedByModule: Record<string, RawProethicaSessionItem[]> =
	Object.fromEntries(
		Object.entries(proethicaLiveEnvelopeByModule).map(([tagId, env]) => [
			tagId,
			normalizeModuleItems(asAdapterItems(env)),
		]),
	);

export const proethicaLiveSummariesByModule: Record<
	string,
	ProethicaModuleSummary | undefined
> = Object.fromEntries(
	Object.entries(normalizedByModule).map(([tagId, normalized]) => [
		tagId,
		extractModuleSummary(normalized),
	]),
);

// Default principles reference, read off the Module 3 EXPLORE item. Surfaced for
// the standalone Principles tab, which renders outside any session; in-session
// screens read principles straight from the active session instead.
type PrincipleList = { title: string; description: string }[];

export const proethicaDefaultPrinciples: PrincipleList =
	normalizeModuleItems(asAdapterItems(proethicaDefaultEnvelope))
		.map((item) => (item.content as { principles?: PrincipleList }).principles)
		.find((principles): principles is PrincipleList =>
			Boolean(principles?.length),
		) ?? [];

const isLiveProethicaItem = (item: unknown): boolean =>
	Boolean((item as { metadata?: { ui_type?: string } })?.metadata?.ui_type);

const deriveModuleLabel = (
	items: NewFormatModuleItem[],
	response: { examName?: string },
): string => {
	const fromName = response.examName?.match(/Module\s+(\d+)/i)?.[1];
	const moduleNumber = fromName ?? findModuleOverview(items)?.moduleNumber;
	return `Module ${moduleNumber ?? 1}`;
};

/**
 * Single convergence point for ProEthica session data: live create-session
 * responses are converted to legacy `SessionItem`s so components render
 * unchanged; any already-legacy response (other demo exams, the live API once
 * wired) passes through untouched.
 */
export const normalizeProethicaSessionResponse = <
	T extends {
		sessionItems?: SessionItem[] | LiveSessionItem[];
		examName?: string;
	},
>(
	response: T,
): Omit<T, "sessionItems"> & { sessionItems: SessionItem[] } => {
	const items = response.sessionItems ?? [];
	if (!(items as unknown[]).some(isLiveProethicaItem)) {
		return { ...response, sessionItems: items as SessionItem[] };
	}

	const raw = items as unknown as NewFormatModuleItem[];
	if (raw.some(isProethicaTestItem)) {
		const { sessionItems } = buildProethicaTest(raw);
		return { ...response, sessionItems };
	}

	const { sessionItems } = buildProethicaModule({
		raw,
		moduleLabel: deriveModuleLabel(raw, response),
	});
	return { ...response, sessionItems };
};
